import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { t } from '@lingui/core/macro';
import Stack from '@mui/material/Stack';
import { PageHeader } from '@/common/components/page-header/PageHeader.tsx';
import { Trans } from '@lingui/react/macro';
import { dispenserQueries } from '@/features/dispensers/queries.ts';
import { useInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { DetailCard } from '@/common/components/detail-card/DetailCard.tsx';
import { EventsTimeline } from '@/features/events/components/events-timeline/EventsTimeline.tsx';
import { z } from 'zod';
import { eventTypeSchema } from '@/features/events/types/EventType.ts';
import { apiDate } from '@/common/api/schemas.ts';
import { SidebarContext } from '@/common/components/sidebar/SidebarContext.ts';
import { EventsTimelineGroupSidebar } from '@/features/events/components/events-timeline-group-sidebar/EventsTimelineGroupSidebar.tsx';
import { usePreservedValue } from '@/common/hooks/usePreservedValue.ts';
import { EventsTimelineQueryStatus } from '@/features/events/components/events-timeline-query-status/EventsTimelineQueryStatus.tsx';
import { useState } from 'react';
import Container from '@mui/material/Container';

const searchSchema = z.object({
  group: z
    .object({
      eventType: eventTypeSchema,
      startTime: apiDate(),
      endTime: apiDate(),
    })
    .optional(),
});

export const Route = createFileRoute('/dispensers/$dispenserId/timeline')({
  component: RouteComponent,
  validateSearch: searchSchema,
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(dispenserQueries.detail(params.dispenserId)),
      context.queryClient.ensureInfiniteQueryData(
        dispenserQueries.timelineInfinite(params.dispenserId, { limit: TIMELINE_PAGE_SIZE })
      ),
    ]);

    return {
      breadcrumb: t`Timeline`,
    };
  },
});

const TIMELINE_PAGE_SIZE = 10;

function RouteComponent() {
  const params = Route.useParams();
  const navigate = useNavigate();
  const { group: selectedGroup } = Route.useSearch();
  const preservedGroup = usePreservedValue(selectedGroup);
  const { data: dispenser } = useSuspenseQuery(dispenserQueries.detail(params.dispenserId));
  const {
    data: timelinePages,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    fetchNextPage,
    isError,
    status,
    refetch,
  } = useInfiniteQuery(
    dispenserQueries.timelineInfinite(params.dispenserId, { limit: TIMELINE_PAGE_SIZE })
  );
  const dispenserSn = dispenser.sn;

  const [sidebarKey, setSidebarKey] = useState(0);

  const timelineItems = timelinePages?.pages.flatMap((page) => page.items);

  return (
    <Container maxWidth="lg" disableGutters>
      <Stack gap={4}>
        <PageHeader
          title={<Trans>Event Timeline</Trans>}
          subtitle={<Trans>{dispenserSn} - Activity history and events</Trans>}
        />
        <DetailCard title={<Trans>Service Log</Trans>}>
          <EventsTimelineQueryStatus status={status} data={timelineItems} onRefetch={refetch}>
            <EventsTimeline
              items={timelineItems ?? []}
              canLoadMore={hasNextPage}
              isLoading={isFetchingNextPage}
              onLoadMore={() => !isFetching && fetchNextPage()}
              loadingError={isError ? <Trans>Failed to load events</Trans> : null}
              loadingErrorDescription={
                isError ? (
                  <Trans>There has been an error when loading next page of events.</Trans>
                ) : null
              }
              onShowChildEvents={(item) => {
                setSidebarKey((i) => i + 1);
                return navigate({
                  to: '.',
                  from: Route.fullPath,
                  params,
                  search: {
                    group: {
                      eventType: item.eventType,
                      startTime: item.startTime,
                      endTime: item.endTime,
                    },
                  },
                });
              }}
            />
          </EventsTimelineQueryStatus>
        </DetailCard>
        <SidebarContext
          value={{
            id: 'timeline-group-sidebar',
            isOpen: !!selectedGroup,
            onClose: () => navigate({ to: '.', from: Route.fullPath, params }),
          }}
        >
          {preservedGroup && (
            <EventsTimelineGroupSidebar
              key={sidebarKey} // this key is here to trigger useDeferredValue inside the sidebar
              dispenserId={params.dispenserId}
              eventType={preservedGroup.eventType}
              startTime={preservedGroup.startTime}
              endTime={preservedGroup.endTime}
            />
          )}
        </SidebarContext>
      </Stack>
    </Container>
  );
}
